// src/components/resources/tools/generators/AiChat/AiChatContext.tsx

'use client'

import { createContext, useContext, useMemo, useReducer, type ReactNode } from 'react'
import { usePreviewDeferral, useCommitStaleRef } from '../shared/usePreviewDeferral'
import type {
  AiChatAction,
  AiChatMessage,
  AiChatSettings,
  AiChatState,
  AiProvider,
} from './AiChat.types'

const AiChatStateContext = createContext<AiChatState | undefined>(undefined)
const AiChatDispatchContext = createContext<React.Dispatch<AiChatAction> | undefined>(undefined)

/**
 * The provider preset a landing-page variant (chatgpt / claude / gemini)
 * booted with, exposed so `resetConversation()` reapplies the advertised
 * provider instead of falling back to the umbrella default. Umbrella mounts
 * surface `undefined`, which the reset path treats as a plain reset.
 */
interface AiChatInitialPreset {
  provider?: AiProvider
}
const AiChatInitialPresetContext = createContext<AiChatInitialPreset>({})

const createId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `ai-chat-${Math.random().toString(16).slice(2)}-${Date.now()}`
}

/**
 * Per-provider settings applied on `SET_PROVIDER`. Only the fields that should
 * change when switching assistant are listed — `modelLabel` is reset to the
 * provider's flagship so an iMessage-style "wrong model on the wrong app"
 * mismatch can't survive a provider switch, while user identity and toggles
 * are preserved.
 */
const providerDefaults: Record<AiProvider, Partial<AiChatSettings>> = {
  chatgpt: { provider: 'chatgpt', modelLabel: 'ChatGPT 4o' },
  claude: { provider: 'claude', modelLabel: 'Claude Opus 4.1' },
  gemini: { provider: 'gemini', modelLabel: '2.5 Flash' },
}

const createInitialMessages = (): AiChatMessage[] => [
  {
    id: 'ai-chat-message-1',
    role: 'user',
    content: 'Write three ad headline variations for a project-management app aimed at startup founders. Keep them punchy.',
  },
  {
    id: 'ai-chat-message-2',
    role: 'assistant',
    content: [
      'Here are three punchy headline directions, each leaning on a different angle a founder cares about:',
      '',
      '1. **Ship faster, not harder** — Plan, assign, and launch in one place built for teams that move.',
      '2. **Your roadmap, finally on the rails** — Turn scattered to-dos into a plan investors love to see.',
      '3. **Less standup, more shipping** — Async updates that keep everyone aligned without the meeting tax.',
      '',
      'Want me to tailor these to a specific channel — paid search, LinkedIn, or a landing-page hero?',
    ].join('\n'),
  },
]

const createInitialSettings = (): AiChatSettings => ({
  provider: 'chatgpt',
  theme: 'light',
  modelLabel: 'ChatGPT 4o',
  userInitials: 'JD',
  userAvatarColor: 'bg-indigo-500',
  showActions: true,
  showAvatars: true,
  showHeader: true,
})

const createInitialState = (): AiChatState => ({
  settings: createInitialSettings(),
  messages: createInitialMessages(),
  activeMessageId: 'ai-chat-message-2',
  isExporting: false,
})

const aiChatReducer = (state: AiChatState, action: AiChatAction): AiChatState => {
  switch (action.type) {
    case 'SET_PROVIDER': {
      return {
        ...state,
        settings: { ...state.settings, ...providerDefaults[action.payload] },
      }
    }
    case 'UPDATE_SETTINGS': {
      return { ...state, settings: { ...state.settings, ...action.payload } }
    }
    case 'ADD_MESSAGE': {
      return {
        ...state,
        messages: [...state.messages, action.payload],
        activeMessageId: action.payload.id,
      }
    }
    case 'UPDATE_MESSAGE': {
      return {
        ...state,
        messages: state.messages.map(message =>
          message.id === action.payload.id ? { ...message, ...action.payload.changes } : message,
        ),
      }
    }
    case 'REMOVE_MESSAGE': {
      const filtered = state.messages.filter(message => message.id !== action.payload)
      const last = filtered[filtered.length - 1]
      return {
        ...state,
        messages: filtered,
        activeMessageId: state.activeMessageId === action.payload ? (last ? last.id : null) : state.activeMessageId,
      }
    }
    case 'MOVE_MESSAGE': {
      const index = state.messages.findIndex(message => message.id === action.payload.id)
      if (index === -1) return state
      const target = action.payload.direction === 'up' ? index - 1 : index + 1
      if (target < 0 || target >= state.messages.length) return state
      const messages = [...state.messages]
      const [moved] = messages.splice(index, 1)
      if (!moved) return state
      messages.splice(target, 0, moved)
      return { ...state, messages }
    }
    case 'SET_ACTIVE_MESSAGE': {
      return { ...state, activeMessageId: action.payload }
    }
    case 'SET_EXPORTING': {
      return { ...state, isExporting: action.payload }
    }
    case 'RESET_CONVERSATION': {
      const base = createInitialState()
      if (!action.payload?.provider) return base
      return aiChatReducer(base, { type: 'SET_PROVIDER', payload: action.payload.provider })
    }
    default: {
      // Compile-time exhaustiveness: adding an `AiChatAction` variant without a
      // case above fails to typecheck here (mirrors the route's variantPreset
      // guard). Unreachable from typed dispatch.
      const _exhaustive: never = action
      return _exhaustive
    }
  }
}

export interface AiChatProviderProps {
  children: ReactNode
  /** Boot the generator pre-configured for a specific assistant (landing variants). */
  initialProvider?: AiProvider
}

function applyInitialPreset(base: AiChatState, initialProvider: AiProvider | undefined): AiChatState {
  if (!initialProvider) return base
  return aiChatReducer(base, { type: 'SET_PROVIDER', payload: initialProvider })
}

export function AiChatProvider({ children, initialProvider }: AiChatProviderProps): JSX.Element {
  const [state, dispatch] = useReducer(aiChatReducer, undefined, () =>
    applyInitialPreset(createInitialState(), initialProvider),
  )

  const initialPreset = useMemo<AiChatInitialPreset>(() => ({ provider: initialProvider }), [initialProvider])

  return (
    <AiChatStateContext.Provider value={state}>
      <AiChatDispatchContext.Provider value={dispatch}>
        <AiChatInitialPresetContext.Provider value={initialPreset}>
          {children}
        </AiChatInitialPresetContext.Provider>
      </AiChatDispatchContext.Provider>
    </AiChatStateContext.Provider>
  )
}

/**
 * Re-provides the AI chat state to its subtree from a `useDeferredValue`
 * snapshot so keystrokes in the Controls column stay responsive — the heavy
 * preview re-renders at a lower priority and converges a frame later. Mirrors
 * the FakeChat deferred boundary; `staleRef` flips `true` while the snapshot
 * trails live state so an export can wait for it to settle before capture.
 */
export function DeferredAiChatStateProvider({
  children,
  staleRef,
}: {
  children: ReactNode
  staleRef?: React.MutableRefObject<boolean>
}): JSX.Element {
  const liveState = useAiChatState()
  const { deferred: deferredState, isStale } = usePreviewDeferral(liveState)
  // Commit staleness into the caller-provided ref from a layout effect (pre-paint)
  // so the export gate (`waitForStable`) can't green-light a Download in the gap a
  // passive effect would leave after a keystroke (shared with useStaleRef so the
  // semantics can't drift). See useCommitStaleRef.
  useCommitStaleRef(isStale, staleRef)

  return <AiChatStateContext.Provider value={deferredState}>{children}</AiChatStateContext.Provider>
}

export function useAiChatState(): AiChatState {
  const context = useContext(AiChatStateContext)
  if (!context) {
    throw new Error('useAiChatState must be used within an AiChatProvider')
  }
  return context
}

export function useAiChatActions(): {
  setProvider: (provider: AiProvider) => void
  updateSettings: (changes: Partial<AiChatSettings>) => void
  addMessage: (message: AiChatMessage) => void
  updateMessage: (id: string, changes: Partial<AiChatMessage>) => void
  removeMessage: (id: string) => void
  moveMessage: (id: string, direction: 'up' | 'down') => void
  setActiveMessage: (id: string | null) => void
  setExporting: (isExporting: boolean) => void
  resetConversation: () => void
  generateMessageId: () => string
} {
  const dispatch = useContext(AiChatDispatchContext)
  if (!dispatch) {
    throw new Error('useAiChatActions must be used within an AiChatProvider')
  }
  const initialPreset = useContext(AiChatInitialPresetContext)

  return useMemo(
    () => ({
      setProvider: (provider: AiProvider) => dispatch({ type: 'SET_PROVIDER', payload: provider }),
      updateSettings: (changes: Partial<AiChatSettings>) => dispatch({ type: 'UPDATE_SETTINGS', payload: changes }),
      addMessage: (message: AiChatMessage) => dispatch({ type: 'ADD_MESSAGE', payload: message }),
      updateMessage: (id: string, changes: Partial<AiChatMessage>) =>
        dispatch({ type: 'UPDATE_MESSAGE', payload: { id, changes } }),
      removeMessage: (id: string) => dispatch({ type: 'REMOVE_MESSAGE', payload: id }),
      moveMessage: (id: string, direction: 'up' | 'down') => dispatch({ type: 'MOVE_MESSAGE', payload: { id, direction } }),
      setActiveMessage: (id: string | null) => dispatch({ type: 'SET_ACTIVE_MESSAGE', payload: id }),
      setExporting: (isExporting: boolean) => dispatch({ type: 'SET_EXPORTING', payload: isExporting }),
      resetConversation: () => dispatch({ type: 'RESET_CONVERSATION', payload: { provider: initialPreset.provider } }),
      generateMessageId: () => createId(),
    }),
    [dispatch, initialPreset],
  )
}
